import apiClient, { ApiResponse } from "./client";
import { config } from "../config";

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: "admin" | "user";
    permissions: string[];
  };
  expiresIn: number;
}

export interface RefreshTokenResponse {
  token: string;
  expiresIn: number;
}

export interface MicrosoftLoginRequest {
  code: string;
  state?: string;
  codeVerifier?: string;
}

export const authApi = {
  // Standard email/password login
  login: async (
    credentials: LoginRequest
  ): Promise<ApiResponse<LoginResponse>> => {
    try {
      const response = await apiClient.post<LoginResponse>(
        config.endpoints.auth.login,
        credentials
      );
      return response;
    } catch (error) {
      console.error("Login API error:", error);
      return {
        success: false,
        error: "Login service unavailable. Please check backend configuration.",
      };
    }
  },

  // Microsoft SSO login - send code to backend for token exchange
  microsoftLogin: async (
    request: MicrosoftLoginRequest
  ): Promise<ApiResponse<LoginResponse>> => {
    try {
      // This will send the authorization code to backend
      // Backend will exchange it with Microsoft for access token and user info
      // Then return our own JWT token
      const response = await apiClient.post<LoginResponse>(
        config.endpoints.auth.microsoft,
        request
      );
      return response;
    } catch (error) {
      console.error("Microsoft login API error:", error);
      // In production, this should throw the error
      // For development, return error response
      return {
        success: false,
        error:
          "Microsoft login service unavailable. Please check backend configuration.",
      };
    }
  },

  // Refresh token
  refreshToken: async (): Promise<ApiResponse<RefreshTokenResponse>> => {
    try {
      const response = await apiClient.post<RefreshTokenResponse>(
        config.endpoints.auth.refresh
      );
      return response;
    } catch (error) {
      console.error("Refresh token error:", error);
      throw error;
    }
  },

  // Logout
  logout: async (): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.post<void>(config.endpoints.auth.logout);
      return response;
    } catch (error) {
      console.error("Logout error:", error);
      // Still return success for demo
      return {
        success: true,
        data: undefined,
        message: "Đăng xuất thành công",
      };
    }
  },

  // Get current user profile
  getProfile: async (): Promise<ApiResponse<LoginResponse["user"]>> => {
    try {
      const response = await apiClient.get<LoginResponse["user"]>(
        config.endpoints.auth.profile
      );
      return response;
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  },

  // Verify token
  verifyToken: async (): Promise<ApiResponse<{ valid: boolean }>> => {
    try {
      const response = await apiClient.get<{ valid: boolean }>(
        config.endpoints.auth.verify
      );
      return response;
    } catch (error) {
      console.error("Verify token error:", error);
      return {
        success: false,
        data: { valid: false },
      };
    }
  },
};
